// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

const PHONE_ENCOUNTER_CODE = 'phone-encounter';

export function isPhoneEncounter(encounter: Encounter): boolean {
  return Boolean(
    encounter.type?.some((type) =>
      type.coding?.some((coding) => coding.code === PHONE_ENCOUNTER_CODE || coding.display === 'Phone Encounter')
    )
  );
}

export function usePatientPhoneEncounters(patient: Patient | undefined): {
  encounters: Encounter[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEncounters = useCallback(async () => {
    if (!patient?.id) {
      setEncounters([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await medplum.searchResources('Encounter', {
        subject: `Patient/${patient.id}`,
        _count: '100',
        _sort: '-date',
      });
      setEncounters(results.filter(isPhoneEncounter));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load phone encounters'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetchEncounters().catch(console.error);
  }, [fetchEncounters]);

  return { encounters, loading, error, refresh: fetchEncounters };
}
